import { AssetUrlPipe } from "../../core/pipes/asset-url.pipe";
import { CommonModule } from "@angular/common";
import { Component, ElementRef, HostListener, inject, Input, OnDestroy, OnInit, } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { KeycloakService } from "keycloak-angular";
import { filter, Subscription } from "rxjs";
import { AgenticAiService } from "../../core/services/agentic-ai.service";
import { AuthRealmService } from "../../core/services/keycloak/auth-realm.service";
import { RestService } from "../../core/services/rest.service";
import { ToasterService } from "../../core/services/toaster-service";
import { ConfigService } from "../../core/services/config/config.service";
import { clearAuthHandshakeStorage } from "../../core/services/keycloak/auth-token-storage";

/**
 * HeaderComponent
 *
 * This component represents the application header and manages user authentication status
 * using Keycloak. It provides login and logout functionality and adjusts the UI based on
 * whether the user is authenticated.
 */
@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: true,
  imports: [AssetUrlPipe,  MatTooltipModule, CommonModule ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  private modalService = inject(NgbModal);
  private routerEventsSub!: Subscription;

  @Input() title = '';
  @Input() projectType: string = '';
  
  paramIds: any = {};
  isAuthenticated: boolean = false;
  isProfileMenuOpen: boolean = false;
  isAgentic:boolean = false;
  isTogglingAiApproach = false;
  isAgenticEnabled: boolean = true;
  isTelemetryDashboardEnabled: boolean = false;
  projectName:string = "";
  selectedTab: string = 'projects'; // Default selected tab

  configs = {
    initials: "",
    name: "",
    email: "",
    type: "Agentic AI Approach",
    projectId:""
  };

  constructor(
    private keycloakService: KeycloakService,
    protected readonly route: Router,
    private router: ActivatedRoute,
    private rest: RestService,
    private elementRef: ElementRef,
    private agenticAiService: AgenticAiService,
    private authService: AuthRealmService,
    private configService: ConfigService,
    public toaster: ToasterService
  ) {
    this.router.paramMap.subscribe((params) => {
      this.projectName = params.get("name") || '';
    });
  }

  userProfile:any;
  /**
   * Lifecycle hook that is called after the component is initialized.
   * Checks the user's authentication status using the Keycloak service.
   */
  async ngOnInit() {
    this.isTelemetryDashboardEnabled =
      this.configService.getConfig("isTelemetryDashboardEnabled") ?? false;
    this.router.params.subscribe((params: any) => {
      this.paramIds = params;
    });
    this.isAuthenticated = await this.keycloakService.isLoggedIn();

    this.userProfile = this.authService.getUserProfile();
    this.configs.name = this.userProfile?.name;
    this.configs.email = this.userProfile?.email;
    this.configs.initials = this.getInitials(this.configs.name);
    this.configs.projectId = this.paramIds?.id;

    // Set active tab from current URL on initial load
    this.syncTabWithUrl(this.route.url);

    // Keep tab in sync whenever the user navigates
    this.routerEventsSub = this.route.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.syncTabWithUrl(e.urlAfterRedirects));

    // Subscribe to the AI approach to react to changes
    this.agenticAiService.aiApproach$.subscribe(approach => {
      this.isAgentic = approach === "AGENTIC_AI";
    });

    this.agenticAiService.isAgenticApproach$.subscribe(isAgenticApproach => {
      this.isAgenticEnabled = isAgenticApproach === true;
    });
  }

  ngOnDestroy(): void {
    this.routerEventsSub?.unsubscribe();
  }

  /** Map the current URL to the correct tab identifier. */
  private syncTabWithUrl(url: string): void {
    if (url.includes('telemetry-dashboard')) {
      this.selectedTab = 'telemetry';
    } else {
      this.selectedTab = 'projects';
    }
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isProfileMenuOpen = false;
    }
  }

  navigateToHome() {
    this.route.navigate([""]); // Update with your actual home route
  }

  /**
   * Handles the login or logout functionality based on the user's authentication status.
   * - If the user is authenticated, logs them out and clears session data.
   * - If the user is not authenticated, initiates the login process.
   */
  async onLogOrLogout() {
    if (this.isAuthenticated) {

      if(this.route.url.includes('editor-view')) {
        this.rest.unlockScript(this.paramIds.scriptId).subscribe((res: any) => {
          this.logout();
        }, err => {
          this.logout();
        });
      } else {
        this.logout();
      }
      
    } else {
      // Directly initiate login if not authenticated
      await this.keycloakService.login({
        redirectUri: this.configService.buildAppUrl("admin/organization"),
      });
    }
  }

  async logout() {
    sessionStorage.removeItem("selectedRealm");
    sessionStorage.removeItem("selectedOrganizationName");
    localStorage.removeItem("selectedOrganizationName");
    localStorage.removeItem("selectedOrganizationRealm");
    clearAuthHandshakeStorage();

    const redirectUri = this.configService.getLogoutRedirectUrl();
    const keycloak = this.keycloakService.getKeycloakInstance();
    if (keycloak?.createLogoutUrl) {
      window.location.assign(keycloak.createLogoutUrl({ redirectUri }));
      return;
    }
    await this.keycloakService.logout(redirectUri);
  }

  navLinkClick(type: string) {
    if(type === 'all_projects') {
      // Clear the service state so the aiApproach$ subscription also reflects false.
      // Setting only the local flag would be overridden when any route/resolver
      // re-emits the stored BehaviorSubject value.
      this.agenticAiService.setAiApproach(null);
      this.route.navigate(['user/project']);
    }
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
    if (tab === 'projects') {
      this.route.navigate(['user/project']);
    } else if (tab === 'telemetry') {
      this.route.navigate(['telemetry-dashboard']);
    }
  }

  toggleProfileMenu(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
  }

  signOut(): void {
    this.isProfileMenuOpen = false;
    this.onLogOrLogout();
  }

  toggle() {
    this.isTogglingAiApproach = true;
    this.isAgentic = !this.isAgentic;
    //change ai approach
    let aiApproach = this.isAgentic ? "AGENTIC_AI" : "NON_AGENTIC_AI";
    this.rest.aiApproach(this.paramIds?.id, aiApproach)
    .subscribe({
      next: (res: any) => {
        console.log('AI Approach set to :', res);
        this.agenticAiService.setAiApproach(res?.data?.aiApproach);
        // this.toaster.pop({
        //   type: "success",
        //   text: res?.data?.aiApproach === "AGENTIC_AI" ? MessageEnum.AI_APPROACH_SET_TO_AGENTIC : MessageEnum.AI_APPROACH_SET_TO_NON_AGENTIC,
        // });
        this.isTogglingAiApproach = false;
        window.location.reload();
      },
      error: (err) => {
        console.error('Error selecting ai approach:', err);
        this.isTogglingAiApproach = false;
      }
    });
  }

  /**
   * Generates initials from a full name.
   * @param name The user's full name.
   * @returns A string with the user's initials (e.g., "CC" for "Camilla Carter").
   */
  getInitials(name: string): string {
    if (!name) {
      return '';
    }
    const nameParts = name.trim().split(' ').filter(part => part);
    const firstInitial = nameParts.length > 0 ? nameParts[0][0] : '';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : '';
    return (firstInitial + lastInitial).toUpperCase();
  }
}
